import { useMemo } from "react";
import { PERMISSIONS } from "./constants";
import { RESOURCE_PERMISSIONS } from "./resourcePermissions";
import { useAuth } from "./useAuth";

export const useNavPermissions = () => useAuth();

/**
 * Sidebar item visibility flags for the POS application.
 * Company principals see everything; user principals see only what their permissions allow.
 */
export const useNavbarItemPermissions = () => {
  const { hasAnyPermission, isCompanyPrincipal } = useAuth();

  return useMemo(() => {
    if (isCompanyPrincipal) {
      return {
        canAccessHome: true,
        canAccessUsersAndRules: true,
        canAccessStaffUsersAndRoles: true,
        canAccessWebsiteUsers: true,
        canAccessGeneralCodes: true,
        canAccessProducts: true,
        canAccessCategories: true,
        canAccessProductList: true,
        canAccessTestimonials: true,
        canAccessContactInquiries: true,
        canAccessSalesSessions: true,
        canAccessOrderSearch: true,
        canAccessPromotions: true,
        canAccessReturns: true,
        canAccessInventory: true,
        canAccessReports: true,
        canAccessSettings: true,
        canAccessCommerceReviews: true,
        canAccessCustomerRisk: true,
        canAccessCommerceSettings: true,
        canAccessCommerce: true,
      };
    }

    const canAccessCategories = hasAnyPermission(RESOURCE_PERMISSIONS.CATEGORIES);

    const canAccessProductList = hasAnyPermission(RESOURCE_PERMISSIONS.PRODUCTS);

    const canAccessTestimonials = hasAnyPermission([
      ...RESOURCE_PERMISSIONS.HOMEPAGE,
      PERMISSIONS.PRODUCTS.VIEW_ANY,
    ]);
    const canAccessPromotions = hasAnyPermission(RESOURCE_PERMISSIONS.PROMOTIONS);

    const canAccessProducts =
      canAccessCategories ||
      canAccessProductList ||
      canAccessTestimonials ||
      canAccessPromotions;
    const canAccessContactInquiries = hasAnyPermission(
      RESOURCE_PERMISSIONS.CONTACT_INQUIRIES,
    );

    const canAccessStaffUsersAndRoles = hasAnyPermission(
      RESOURCE_PERMISSIONS.USERS_ROLES,
    );
    const canAccessWebsiteUsers = hasAnyPermission(
      RESOURCE_PERMISSIONS.WEBSITE_USERS,
    );
    const canAccessUsersAndRules =
      canAccessStaffUsersAndRoles || canAccessWebsiteUsers;
    const canAccessHome = hasAnyPermission(RESOURCE_PERMISSIONS.DASHBOARD);

    return {
      canAccessHome,
      canAccessUsersAndRules,
      canAccessStaffUsersAndRoles,
      canAccessWebsiteUsers,
      canAccessGeneralCodes: false,
      canAccessProducts,
      canAccessCategories,
      canAccessProductList,
      canAccessTestimonials,
      canAccessContactInquiries,
      canAccessSalesSessions: false,
      canAccessOrderSearch: hasAnyPermission(RESOURCE_PERMISSIONS.ORDERS),
      canAccessPromotions,
      canAccessReturns: false,
      canAccessInventory: hasAnyPermission(RESOURCE_PERMISSIONS.INVENTORY),
      canAccessReports: hasAnyPermission(RESOURCE_PERMISSIONS.REPORTS),
      canAccessSettings: hasAnyPermission([
        ...RESOURCE_PERMISSIONS.BANNERS,
        ...RESOURCE_PERMISSIONS.COMMERCE_SETTINGS,
      ]),
      canAccessCommerceReviews: hasAnyPermission(RESOURCE_PERMISSIONS.REVIEWS),
      canAccessCustomerRisk: hasAnyPermission(RESOURCE_PERMISSIONS.CUSTOMER_RISK),
      canAccessCommerceSettings: hasAnyPermission(
        RESOURCE_PERMISSIONS.COMMERCE_SETTINGS,
      ),
      canAccessCommerce: hasAnyPermission([
        ...RESOURCE_PERMISSIONS.ORDERS,
        ...RESOURCE_PERMISSIONS.PROMOTIONS,
        ...RESOURCE_PERMISSIONS.INVENTORY,
        ...RESOURCE_PERMISSIONS.REVIEWS,
        ...RESOURCE_PERMISSIONS.CUSTOMER_RISK,
        ...RESOURCE_PERMISSIONS.REPORTS,
        ...RESOURCE_PERMISSIONS.COMMERCE_SETTINGS,
      ]),
    };
  }, [hasAnyPermission, isCompanyPrincipal]);
};
